/**
 * OwalSearchSynonym.js
 *
 * @description Contains a class for mapping words to a specific search term.
 */

/**
 * Defines a root search term and a list of synonyms to map to that word.
 *
 * @export
 * @class OwalSearchSynonym
 */
export class OwalSearchSynonym {
  /**
   * Creates an instance of OwalSearchSynonym.
   * @param {String} rootWord The value we want to map to.
   * @param {Array<String>} synonyms A list of strings to map to.
   * @param {OwalSearchContext} synonymContext An OwalSearchContext describing when to apply the syonyms.
   * @memberof OwalSearchSynonym
   */
  constructor ({ rootWord, synonyms, context } = {}) {
    this.rootWord = rootWord
    this.synonyms = synonyms
    this.synonymContext = context
  }

  /**
   * Determines if the synonym applies to the supplied object.
   * If the synonym object has no context, all objects return true.
   *
   * @param {Object} object An object to check for synonym relevance.
   * @returns Boolean
   * @memberof OwalSearchSynonym
   */
  appliesToObject (object) {
    if (!this.context) {
      return true
    }
    return this.context.appliesToObject(object)
  }

  /**
   * Determines if the synonym applies to the supplied key.
   * If the synonym object has no context, all inputs return true.
   *
   * @param {String} key A key to check for synonym relevance.
   * @returns Boolean
   * @memberof OwalSearchSynonym
   */
  appliesToKey (key) {
    if (!this.context) {
      return true
    }
    // console.debug(`Checking if ${this.rootWord} applies to ${key}`)
    return this.context.appliesToKey(key)
  }

  /**
   * Determines if the synonym applies to the supplied key.
   * If the synonym object has no context, all inputs return true.
   *
   * @param {String} key A key to check for synonym relevance.
   * @returns Boolean
   * @memberof OwalSearchSynonym
   */
  appliesToSearchTerm (searchTerm) {
    return this.synonyms.includes(searchTerm)
  }

  /**
   * Returns the root word for a synonym, otherwise returns the inout.
   *
   * @param {String} synonym The word to translate to a root word.
   * @returns A string, either the root word or the input.
   * @memberof OwalSearchSynonym
   */
  rootWordForSynonym (synonym) {
    if (this.synonyms.includes(synonym)) {
      return this.rootWord
    }

    return synonym
  }
}
